import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Package, Upload, X, CheckCircle2, AlertTriangle, Loader2, Camera,
  Truck, FileText, Clock, ChevronRight, Phone, Mail, MapPin, ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ↩️ İADE FORMU — MÜŞTERİ EKRANI (girişsiz, token ile)
 *
 * Müşteri panele giriş yapmaz; müdürün gönderdiği linkle gelir. Bu sayfa
 * müşterinin gördüğü TEK yüzümüz olduğu için kurumsal kimlik burada başlar:
 * logo, resmî ünvan, adres. Mobilde de rahat doldurulmalı — bayi çoğu zaman
 * telefondan açıyor.
 *
 * Form gönderildikten sonra aynı sayfa TAKİP moduna döner (aşama çubuğu).
 */
export default function IadeForm() {
  const { token } = useParams();
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');

  const [sebep, setSebep] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [fotolar, setFotolar] = useState([]);
  const [yuklemede, setYuklemede] = useState(false);
  const [iletisim, setIletisim] = useState({ ad: '', telefon: '' });
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const dosyaRef = useRef(null);

  const getir = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/iade/form/${token}`);
      setVeri(data.veri);
    } catch (e) {
      setHata(e.response?.data?.detail || 'Bağlantı geçersiz veya süresi dolmuş.');
    } finally {
      setYukleniyor(false);
    }
  }, [token]);

  useEffect(() => { getir(); }, [getir]);

  const seciliSebep = (veri?.sebepler || []).find((s) => s.kod === sebep);
  const fotoZorunlu = seciliSebep?.foto_zorunlu;
  const gonderilebilir =
    sebep && aciklama.trim().length >= 10 && (!fotoZorunlu || fotolar.length > 0);

  const dosyaSec = async (e) => {
    const dosyalar = Array.from(e.target.files || []);
    if (!dosyalar.length) return;
    setYuklemede(true);
    for (const d of dosyalar.slice(0, 6 - fotolar.length)) {
      const fd = new FormData();
      fd.append('file', d);
      try {
        const { data } = await axios.post(`${API}/iade/form/${token}/foto`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFotolar((f) => [...f, { url: data.url, ad: d.name }]);
      } catch (err) {
        setHata(err.response?.data?.detail || `${d.name} yüklenemedi`);
      }
    }
    setYuklemede(false);
    if (dosyaRef.current) dosyaRef.current.value = '';
  };

  const gonder = async () => {
    setGonderiliyor(true);
    setHata('');
    try {
      const { data } = await axios.post(`${API}/iade/form/${token}`, {
        sebep_kodu: sebep,
        aciklama: aciklama.trim(),
        fotograflar: fotolar.map((f) => f.url),
        iletisim,
      });
      setVeri(data.veri);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setHata(e.response?.data?.detail || 'Talep gönderilemedi, lütfen tekrar deneyin.');
    } finally {
      setGonderiliyor(false);
    }
  };

  if (yukleniyor) {
    return (
      <Kabuk>
        <div className="py-24 text-center text-slate-500">
          <Loader2 className="animate-spin mx-auto mb-3" size={30} />
          Bilgiler yükleniyor…
        </div>
      </Kabuk>
    );
  }

  if (!veri) {
    return (
      <Kabuk>
        <div className="py-20 text-center">
          <AlertTriangle className="mx-auto mb-4 text-amber-500" size={44} />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Bağlantı açılamadı</h2>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">{hata}</p>
          <p className="text-xs text-slate-400 mt-4">
            Lütfen ürünü aldığınız firma ile iletişime geçip yeni bağlantı isteyiniz.
          </p>
        </div>
      </Kabuk>
    );
  }

  // ⏳ Süresi dolmuş bağlantı — KAPIDA söyle.
  //
  // Eskiden süre kontrolü yalnız YAZMA yollarındaydı (fotoğraf yükleme, form
  // gönderme). Müşteri formu açıyor, sebebi seçiyor, açıklamayı yazıyor,
  // fotoğrafı yüklerken "bağlantının süresi dolmuş" hatası alıyordu — emeği
  // çöpe gidiyordu. Artık dolmuşsa form hiç gösterilmez.
  if (veri.suresi_doldu && !veri.form_dolduruldu) {
    return (
      <Kabuk firma={veri.firma}>
        <div className="py-16 text-center">
          <AlertTriangle className="mx-auto mb-4 text-amber-500" size={44} />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Bağlantının süresi dolmuş</h2>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Bu iade formu bağlantısı artık geçerli değil. Yeni bir bağlantı için
            bizimle iletişime geçebilirsiniz — talebiniz sistemde duruyor,
            baştan başlamanız gerekmez.
          </p>
          <p className="text-xs font-mono text-slate-400 mt-3">{veri.iade_no}</p>
          <div className="mt-5 inline-flex flex-col gap-1 text-sm">
            {veri.firma?.email && (
              <a href={`mailto:${veri.firma.email}?subject=${encodeURIComponent(`İade formu bağlantısı — ${veri.iade_no}`)}`}
                 className="text-[#D92E1E] font-semibold">{veri.firma.email}</a>
            )}
            {veri.firma?.telefon && (
              <a href={`tel:${veri.firma.telefon}`} className="text-slate-600">{veri.firma.telefon}</a>
            )}
          </div>
        </div>
      </Kabuk>
    );
  }

  const gonderildi = veri.form_dolduruldu;

  return (
    <Kabuk firma={veri.firma}>
      {/* Üst şerit — iade no + aşama */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-5">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
              İade Talep No
            </p>
            <p className="text-lg font-bold text-[#D92E1E] font-mono">{veri.iade_no}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Müşteri</p>
            <p className="text-sm font-semibold text-slate-800">{veri.musteri?.unvan}</p>
          </div>
        </div>
        <AsamaCubugu no={veri.asama_no} ad={veri.asama} durum={veri.durum} />
      </div>

      {/* Ürünler */}
      <Bolum baslik="İade Edilecek Ürünler" ikon={Package}>
        <div className="divide-y divide-slate-100">
          {(veri.kalemler || []).map((k, i) => (
            <div key={i} className="py-2.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <Package size={16} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{k.urun_adi}</p>
                <p className="text-xs text-slate-400 font-mono">{k.sku}</p>
              </div>
              <span className="text-sm font-bold text-slate-700 shrink-0">
                {Number(k.miktar) % 1 === 0 ? Number(k.miktar) : k.miktar} adet
              </span>
            </div>
          ))}
        </div>
      </Bolum>

      {gonderildi ? (
        <Sonuc veri={veri} />
      ) : (
        <>
          {/* Sebep */}
          <Bolum baslik="İade Sebebi" ikon={AlertTriangle} zorunlu>
            <div className="grid sm:grid-cols-2 gap-2">
              {(veri.sebepler || []).map((s) => (
                <button
                  key={s.kod}
                  onClick={() => setSebep(s.kod)}
                  className={`text-left p-3 rounded-xl border-2 transition ${
                    sebep === s.kod
                      ? 'border-[#D92E1E] bg-red-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 ${
                      sebep === s.kod ? 'border-[#D92E1E] bg-[#D92E1E]' : 'border-slate-300'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{s.ad}</p>
                      <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{s.aciklama}</p>
                      {s.foto_zorunlu && (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold
                                         text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          <Camera size={10} /> Fotoğraf gerekli
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Bolum>

          {/* Açıklama */}
          <Bolum baslik="Sorunu Anlatın" ikon={FileText} zorunlu>
            <textarea
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              rows={4}
              placeholder="Ürünle ilgili sorunu mümkün olduğunca ayrıntılı yazınız. Ne zaman fark ettiniz, hangi araçta kullanıldı, nasıl bir belirti var?"
              className="w-full p-3 rounded-xl border border-slate-200 text-sm resize-y
                         focus:outline-none focus:ring-2 focus:ring-[#D92E1E]/30 focus:border-[#D92E1E]"
            />
            <p className={`text-[11px] mt-1 ${aciklama.trim().length < 10 ? 'text-slate-400' : 'text-emerald-600'}`}>
              {aciklama.trim().length < 10
                ? `En az 10 karakter (${aciklama.trim().length}/10)`
                : '✓ Açıklama yeterli'}
            </p>
          </Bolum>

          {/* Kanıt */}
          <Bolum
            baslik="Fotoğraf / Belge"
            ikon={Camera}
            zorunlu={fotoZorunlu}
            not={fotoZorunlu
              ? 'Seçtiğiniz sebep için en az bir fotoğraf gerekiyor.'
              : 'İsteğe bağlı — fotoğraf süreci hızlandırır.'}
          >
            <div className="flex flex-wrap gap-2">
              {fotolar.map((f, i) => (
                <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <img src={f.url} alt={f.ad} className="w-full h-full object-cover"
                       onError={(e) => { e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400 -z-10">
                    {f.ad?.slice(0, 12)}
                  </div>
                  <button
                    onClick={() => setFotolar((p) => p.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white
                               flex items-center justify-center hover:bg-black"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {fotolar.length < 6 && (
                <button
                  onClick={() => dosyaRef.current?.click()}
                  disabled={yuklemede}
                  className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex flex-col
                             items-center justify-center gap-1 text-slate-400 hover:border-[#D92E1E]
                             hover:text-[#D92E1E] transition disabled:opacity-50"
                >
                  {yuklemede ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                  <span className="text-[10px] font-semibold">Ekle</span>
                </button>
              )}
            </div>
            <input ref={dosyaRef} type="file" accept="image/*,.pdf" multiple hidden onChange={dosyaSec} />
          </Bolum>

          {/* İletişim — YALNIZ ad + telefon.
              E-posta sorulmuyor: müşteri bu linki zaten bir e-posta adresinden aldı,
              tekrar yazdırmak gereksiz sürtünme. Sonuç maili cariden çözülüyor.
              Kargo alanları kaldırıldı: form doldurulurken mal henüz gönderilmemiş
              oluyor, bu yüzden hep boş geçiliyordu. */}
          <Bolum baslik="İletişim Bilgileriniz" ikon={Phone}>
            <div className="mb-2.5 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-[11px] text-slate-500">Firma</p>
              <p className="text-sm font-semibold text-slate-800">
                {veri?.musteri?.unvan || '—'}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <Alan etiket="Ad Soyad" deger={iletisim.ad}
                    ayarla={(v) => setIletisim({ ...iletisim, ad: v })} yer="Yetkili kişi" />
              <Alan etiket="Telefon" deger={iletisim.telefon}
                    ayarla={(v) => setIletisim({ ...iletisim, telefon: v })} yer="05xx xxx xx xx" />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Sonuç, size bu bağlantıyı gönderdiğimiz adrese bildirilecektir.
            </p>
          </Bolum>

          {seciliSebep && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200 mb-4">
              <Truck size={16} className="text-slate-500 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600">
                <b>Nakliye:</b>{' '}
                {['arizali', 'hasarli_teslim', 'yanlis_gonderim', 'fazla_gonderim'].includes(sebep)
                  ? 'Bu sebep için nakliye masrafı firmamıza aittir.'
                  : 'Bu sebep için nakliye masrafı tarafınıza aittir.'}
              </p>
            </div>
          )}

          {hata && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 mb-4">
              <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{hata}</p>
            </div>
          )}

          <button
            onClick={gonder}
            disabled={!gonderilebilir || gonderiliyor}
            className="w-full py-3.5 rounded-xl bg-[#D92E1E] text-white font-bold text-sm
                       hover:bg-[#b8271a] disabled:bg-slate-300 disabled:cursor-not-allowed
                       transition flex items-center justify-center gap-2 shadow-sm"
          >
            {gonderiliyor ? <Loader2 size={18} className="animate-spin" /> : <ChevronRight size={18} />}
            İade Talebini Gönder
          </button>
          {!gonderilebilir && (
            <p className="text-[11px] text-slate-400 text-center mt-2">
              {!sebep ? 'Önce iade sebebini seçiniz'
                : aciklama.trim().length < 10 ? 'Açıklama en az 10 karakter olmalı'
                : 'Bu sebep için fotoğraf eklemelisiniz'}
            </p>
          )}
        </>
      )}
    </Kabuk>
  );
}

/* ══════════════════ Alt bileşenler ══════════════════ */

function Kabuk({ children, firma }) {
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b-[3px] border-[#D92E1E]">
        <div className="max-w-3xl mx-auto px-4 py-3.5 flex items-center gap-3">
          <img src="/yetsan-logo-yatay.png" alt="YETSAN" className="h-8 w-auto"
               onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">
              {firma?.unvan || 'YETSAN'}
            </p>
            {firma?.adres && (
              <p className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                <MapPin size={9} /> {firma.adres}
              </p>
            )}
          </div>
          <span className="ml-auto text-[11px] font-bold text-slate-400 uppercase tracking-wide
                           whitespace-nowrap hidden sm:block">
            İade Talep Formu
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 pb-16">{children}</main>

      <footer className="max-w-3xl mx-auto px-4 pb-8 text-center">
        <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck size={12} />
          Bu bağlantı yalnız size özeldir, lütfen paylaşmayınız.
        </p>
        {firma?.email && (
          <p className="text-[11px] text-slate-400 mt-1 flex items-center justify-center gap-1.5">
            <Mail size={11} /> {firma.email}
            {firma.telefon ? ` · ${firma.telefon}` : ''}
          </p>
        )}
      </footer>
    </div>
  );
}

function Bolum({ baslik, ikon: Ikon, zorunlu, not, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        {Ikon && <Ikon size={16} className="text-[#D92E1E]" />}
        <h2 className="text-sm font-bold text-slate-800">{baslik}</h2>
        {zorunlu && <span className="text-[10px] font-bold text-red-600">*</span>}
      </div>
      {not && <p className="text-[11px] text-slate-500 -mt-1.5 mb-2.5">{not}</p>}
      {children}
    </section>
  );
}

function Alan({ etiket, deger, ayarla, yer }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-slate-500">{etiket}</span>
      <input
        value={deger}
        onChange={(e) => ayarla(e.target.value)}
        placeholder={yer}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm
                   focus:outline-none focus:ring-2 focus:ring-[#D92E1E]/30 focus:border-[#D92E1E]"
      />
    </label>
  );
}

function AsamaCubugu({ no, ad, durum }) {
  const asamalar = ['Form', 'Talep alındı', 'İnceleme', 'Karar', 'Tamamlandı'];
  const red = durum === 'RED';
  const iptal = durum === 'IPTAL';
  return (
    <div className="px-5 py-3.5 bg-slate-50">
      <div className="flex items-center gap-1.5">
        {asamalar.map((a, i) => {
          const n = i + 1;
          const gecti = n <= no;
          const renk = iptal ? 'bg-slate-300'
            : red && n === 4 ? 'bg-red-500'
            : gecti ? 'bg-emerald-500' : 'bg-slate-200';
          return (
            <div key={a} className="flex-1">
              <div className={`h-1.5 rounded-full ${renk}`} />
              <p className={`text-[9px] mt-1 text-center truncate ${
                gecti ? 'text-slate-600 font-semibold' : 'text-slate-400'}`}>{a}</p>
            </div>
          );
        })}
      </div>
      <p className={`text-xs font-semibold mt-2 text-center ${
        red ? 'text-red-600' : iptal ? 'text-slate-500' : 'text-emerald-700'}`}>
        {ad}
      </p>
    </div>
  );
}

function Sonuc({ veri }) {
  const s = veri.sonuc;
  const ozet = veri.form_ozeti;
  return (
    <>
      <div className={`rounded-2xl border-2 p-5 mb-4 ${
        s?.karar === 'RED' ? 'bg-red-50 border-red-200'
          : s?.karar === 'KABUL' ? 'bg-emerald-50 border-emerald-200'
          : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex items-start gap-3">
          {s?.karar === 'RED'
            ? <AlertTriangle size={22} className="text-red-600 shrink-0" />
            : s?.karar === 'KABUL'
              ? <CheckCircle2 size={22} className="text-emerald-600 shrink-0" />
              : <Clock size={22} className="text-blue-600 shrink-0" />}
          <div className="min-w-0">
            <h3 className="font-bold text-slate-800 mb-1">
              {s?.karar === 'RED' ? 'İade talebiniz kabul edilemedi'
                : s?.karar === 'KABUL' ? 'İade talebiniz kabul edildi'
                : 'Talebiniz alındı'}
            </h3>
            {s?.karar === 'RED' && (
              <p className="text-sm text-red-800"><b>Gerekçe:</b> {s.gerekce}</p>
            )}
            {s?.karar === 'KABUL' && (
              <>
                <p className="text-sm text-emerald-800"><b>Çözüm:</b> {s.cozum}</p>
                {s.fatura_son_tarih && (
                  <p className="text-sm text-amber-800 mt-2 bg-amber-100 rounded-lg px-3 py-2">
                    <b>İade faturanızı bekliyoruz.</b> Son tarih:{' '}
                    {String(s.fatura_son_tarih).slice(8, 10)}.
                    {String(s.fatura_son_tarih).slice(5, 7)}.
                    {String(s.fatura_son_tarih).slice(0, 4)}
                  </p>
                )}
              </>
            )}
            {!s && (
              <p className="text-sm text-blue-800">
                İade formunu yazdırıp ürünle birlikte gönderiniz. Ürün tarafımıza
                ulaştığında incelenecek ve sonuç size e-posta ile bildirilecektir.
              </p>
            )}
          </div>
        </div>
      </div>

      {ozet && (
        <Bolum baslik="Gönderdiğiniz Bilgiler" ikon={FileText}>
          <dl className="text-sm space-y-1.5">
            <Satir e="Sebep" d={ozet.sebep} />
            <Satir e="Açıklama" d={ozet.aciklama} />
            <Satir e="Ek dosya" d={`${ozet.foto_sayisi} adet`} />
            <Satir e="Nakliye" d={ozet.nakliye} />
          </dl>
        </Bolum>
      )}
    </>
  );
}

function Satir({ e, d }) {
  return (
    <div className="flex gap-3">
      <dt className="text-slate-500 w-24 shrink-0 text-xs pt-0.5">{e}</dt>
      <dd className="text-slate-800 flex-1 min-w-0">{d || '-'}</dd>
    </div>
  );
}
